import { type ClientErrorReport, ROUTES, scrubPii } from "@agw/contracts";
import { ApiError } from "./api";

const PATH = ROUTES.clientErrors.split(" ")[1]!;
const MAX_PER_PAGE = 10;
/** Browser noise that says nothing about our code. */
const IGNORE = [/ResizeObserver loop/i, /^Script error\.?$/i];

const cut = (s: string | undefined, n: number) => (s && s.length > n ? s.slice(0, n) : s);
/** Script URL without query or hash. */
const cleanSource = (s?: string) => cut(s?.split(/[?#]/)[0], 300);

export interface ReporterDeps {
  send: (body: ClientErrorReport) => void;
  build: string;
  path: () => string;
  userAgent: string;
}

/** Builds and sends one report per distinct message, at most 10 a page load. Pure enough to test. */
export function createErrorReporter(deps: ReporterDeps) {
  const seen = new Set<string>();
  return (kind: ClientErrorReport["kind"], err: unknown, at: { source?: string; line?: number; column?: number } = {}) => {
    // Failed API calls already show on screen and in the API logs.
    if (err instanceof ApiError) return false;
    const raw = err instanceof Error ? `${err.name}: ${err.message}` : typeof err === "string" ? err : safeString(err);
    if (!raw || IGNORE.some((re) => re.test(raw))) return false;
    const message = cut(scrubPii(raw), 500)!;
    if (seen.has(message) || seen.size >= MAX_PER_PAGE) return false;
    seen.add(message);
    deps.send({
      kind,
      message,
      stack: cut(err instanceof Error && err.stack ? scrubPii(err.stack) : undefined, 4000),
      source: cleanSource(at.source),
      line: at.line && at.line >= 0 ? at.line : undefined,
      column: at.column && at.column >= 0 ? at.column : undefined,
      path: cut(scrubPii(deps.path()), 200),
      build: deps.build,
      userAgent: cut(deps.userAgent, 300),
    });
    return true;
  };
}

function safeString(v: unknown) {
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}

let current: ReturnType<typeof createErrorReporter> | undefined;

/** For errors React or the router catch before they reach window.onerror. No-op until installed. */
export const reportClientError = (err: unknown) => current?.("error", err);

/** Hooks window errors and unhandled promise rejections. Reporting never throws. */
export function installErrorReporting() {
  const report = (current = createErrorReporter({
    send: (body) => {
      try {
        void fetch(PATH, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), keepalive: true, credentials: "omit" }).catch(() => undefined);
      } catch {
        /* nothing to do */
      }
    },
    build: typeof __APP_BUILD__ === "string" ? __APP_BUILD__ : "dev",
    path: () => window.location.pathname,
    userAgent: navigator.userAgent,
  }));
  window.addEventListener("error", (e) => {
    // Resource load errors (an <img> 404) carry no message; skip them.
    if (!e.message && !e.error) return;
    report("error", e.error ?? e.message, { source: e.filename, line: e.lineno, column: e.colno });
  });
  window.addEventListener("unhandledrejection", (e) => report("rejection", e.reason));
}
